import { Input } from '@/components';
import React, { useCallback, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ChainSelect } from '../ChainSelect';
import styles from './index.module.scss';
import { CommonField } from './CommonField';
import { useGetContractDetail } from './hooks';
import { debounce } from 'lodash';
import { ErrorMessage } from '@hookform/error-message';
import { useActionLoading } from '../ActionSelect/state';

export const ContractUser = () => {
  const {
    control,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useFormContext();
  const [, setActionLoading] = useActionLoading();
  const { mutateAsync, isLoading } = useGetContractDetail();
  const chainId = watch('data.chain_id');
  const contractIdRef = useRef<string>();

  useEffect(() => {
    setActionLoading(isLoading);
  }, [isLoading, setActionLoading]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateContractAddress = useCallback(
    debounce(async (id: string) => {
      try {
        if (id === contractIdRef.current) {
          // 避免重复校验，更改表单其他项会触发contract_id校验，暂时没找到什么办法
          //@ts-ignore
          if (!!errors?.data?.contract_id) {
            setError('data.contract_id', {
              message: 'Please input a valid contract address',
            });
          }
          return;
        }
        contractIdRef.current = id;
        const res = await mutateAsync({ chainId, contractId: id });
        if (!res?.contract?.id) {
          setError('data.contract_id', {
            message: 'Please input a valid contract address',
          });
          return;
        }
        return true;
      } catch (e) {
        setError('data.contract_id', {
          message: 'Please input a valid contract address',
        });
      }
    }, 500),
    [chainId],
  );

  return (
    <div>
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={styles.label}>Contract name</div>
        </div>
        <Controller
          control={control}
          name="data.contract_name"
          render={({ field, fieldState }) => {
            return (
              <Input
                className={styles.input}
                placeholder="Please paste the contract name"
                value={field?.value}
                onChange={field?.onChange}
                error={!!fieldState.error}
                maxLength={30}
                showLength
              />
            );
          }}
          rules={{
            required: true,
          }}
        />
      </div>
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={styles.label}>Contract information</div>
        </div>
        <div className={styles.holderSelectContainer}>
          <Controller
            control={control}
            name="data.chain_id"
            render={({ field }) => (
              <ChainSelect
                value={field.value}
                onChange={val => {
                  field.onChange(val.id);
                  setValue('data.contract_id', '');
                  contractIdRef.current = '';
                }}
              />
            )}
            rules={{
              required: true,
            }}
          />
          <div className={styles.contractInput}>
            <Controller
              control={control}
              name="data.contract_id"
              render={({ field, fieldState }) => {
                return (
                  <Input
                    className={styles.input}
                    placeholder="Paste contract address"
                    value={field?.value}
                    onChange={val => {
                      field?.onChange(val?.toLowerCase());
                    }}
                    error={!!fieldState.error}
                    disabled={!chainId}
                  />
                );
              }}
              rules={{
                required: true,
                validate: validateContractAddress!,
              }}
            />
            <ErrorMessage
              errors={errors}
              name={`data.contract_id`}
              render={({ message }) => {
                return <div className={styles.contractErrorMessage}>{message}</div>;
              }}
            />
          </div>
        </div>
      </div>
      <CommonField />
    </div>
  );
};
